/**
 * Business plan templates — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_office_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const businessPlanTemplatesPack: ToilePack = {
    slug: 'business-plan-templates',
    category: 'office',
    name: 'Business plan templates',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'onepage', label: 'One page', subject: 'a one-page business plan with boxes for problem, solution, market, costs'},
        {key: 'canvas', label: 'Lean canvas', subject: 'a lean canvas grid with nine boxes labelled'},
        {key: 'startup', label: 'Startup', subject: 'a business plan cover and contents page'},
    ],
    styles: [
        {key: 'boxes', label: 'Boxes', style: 'clean boxed layout with thin outlined sections and bold small-caps box titles', palette: 'black ink on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'colour', label: 'Colour blocks', style: 'friendly layout with each section in its own pastel colour block and rounded corners', palette: 'pastel yellow, blue, pink and green on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'slate', label: 'Slate', style: 'sober consulting style with a dark slate title band and fine grey dividers', palette: 'slate blue and grey on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
    ],
    directions: ['dark mode', 'orange accent', 'with chart'],
    directionHint: 'try: dark mode',
    seo: {
        title: 'Business plan template: one-page, lean canvas, startup',
        metaDescription: 'Nine printable business plan templates: a one-page plan, a lean canvas and a startup plan, each in boxed, colour or slate style.',
        h1: 'Business plan template: one-page, lean canvas, startup',
        tagline: 'Three business plan layouts, each in three styles, with a labelled box for each part of the plan.',
        intro: 'A business plan template is the page structure for setting out how a business will work. These nine show a one-page plan with boxes for problem, solution, market and costs; a lean canvas of nine labelled boxes; and a startup plan with a cover and contents page. Only two headings are lettered. Type a twist, such as dark mode, and it is a request.',
        useCases: [
            'a one-page plan to share with a partner',
            'a lean canvas for a new idea',
            'a startup plan with a cover and contents',
            'a plan to review with a small team',
        ],
        faq: [
            {q: 'What is a lean canvas?', a: "A one-page grid of nine boxes that sets out an idea's problem, customers, costs and revenue, among other parts."},
            {q: 'Does the template include the financial numbers?', a: 'No. The boxes are for text, and any figures are grey lines for your own.'},
            {q: 'Which layout is for a longer startup plan?', a: 'The startup layout is a cover page and a contents page, for a longer written plan.'},
            {q: 'Can I add a chart?', a: 'Type it as a twist, such as with chart. It is a request for a chart, not a promised result.'},
        ],
    },
}
